import { toast, useErrorMessage } from '@sentinel/web-core';
import type { ModuleAvailability, ModuleKey, OrganizationLimits, PlanKey } from '@sentinel/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { consoleEndpoints } from '@/api/endpoints';
import { keys } from '@/api/keys';

export type StatusChange = 'suspend' | 'reactivate' | 'archive';

/** One organization in the console: its contract, its modules and every change the platform
 *  makes to them, each with its toast and the data it refreshes. */
export function useOrganization(id: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const onError = (error: unknown) => toast('att', errorMessage(error));
  const saved = (message: string) => () => {
    toast('ok', t(message));
    void queryClient.invalidateQueries({ queryKey: keys.organizations });
  };

  const organization = useQuery({
    queryKey: keys.organization(id),
    queryFn: () => consoleEndpoints.organization(id),
  });
  const modules = useQuery({
    queryKey: keys.modules(id),
    queryFn: () => consoleEndpoints.modules(id),
  });
  const setPlan = useMutation({
    mutationFn: (planKey: PlanKey) => consoleEndpoints.setPlan(id, planKey),
    onSuccess: () => {
      saved('console.organization.planSaved')();
      void queryClient.invalidateQueries({ queryKey: keys.modules(id) });
    },
    onError,
  });
  const setLimits = useMutation({
    mutationFn: (limits: OrganizationLimits) => consoleEndpoints.setLimits(id, limits),
    onSuccess: saved('console.organization.limitsSaved'),
    onError,
  });
  const changeStatus = useMutation({
    mutationFn: (change: StatusChange) => consoleEndpoints.changeStatus(id, change),
    onSuccess: saved('console.organization.statusSaved'),
    onError,
  });
  const setModule = useMutation({
    mutationFn: (input: { key: ModuleKey; availability: ModuleAvailability }) =>
      consoleEndpoints.setModule(id, input.key, input.availability),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.modules(id), data);
      toast('ok', t('console.organization.moduleSaved'));
    },
    onError,
  });
  return { organization, modules, setPlan, setLimits, changeStatus, setModule };
}
